import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { supabaseV2 } from "../../lib/supabase";
import { buildRosterLookup, type EaLinkRosterRow } from "../../lib/ea-name";
import { currentIntlLocale } from "../../lib/i18n";
import { AreaChart, Sparkline } from "../../components/charts";
import { EmptyState } from "../../components/state";
import { RankMedal } from "../../components/primitives";
import { aggregateMatchesStats } from "./aggregateMatchesStats";
import { MatchKpi } from "./MatchKpi";
import { MatchListTable } from "./MatchListTable";
import { PlayerAggTable } from "./PlayerAggTable";
import type { EaMatch } from "./types";
import { ourClubIdOf } from "../../lib/ea-game";

export function FriendlyTab({ teamId, matches, eaClubId }: { teamId: string; matches: EaMatch[]; eaClubId: string }) {
  const { t } = useTranslation("mondofc");
  const [sortBy, setSortBy] = useState<"goals" | "assists" | "rating" | "mom" | "saves">("goals");
  const stats = aggregateMatchesStats(matches, eaClubId);
  const rosterQuery = useQuery({
    queryKey: ["ea-roster-link", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, ea_sports_id, ea_account_handle")
        .eq("team_id", teamId);
      if (error) throw error;
      return (data || []) as EaLinkRosterRow[];
    },
    staleTime: 60_000,
  });
  const rosterLookup = buildRosterLookup(rosterQuery.data || []);
  const goalDiff = stats.goalsFor - stats.goalsAgainst;

  // Forma ultime 5 (matches gia' ordinati desc)
  const formData = matches.slice(0, 5).reverse().map((m) => {
    const ourId = ourClubIdOf(m.data, eaClubId);
    const ourClub = m.data?.clubs?.[ourId];
    const oppId = Object.keys(m.data?.clubs || {}).find((id) => id !== ourId);
    const oppClub = oppId ? m.data.clubs[oppId] : null;
    if (!ourClub || !oppClub) return "-";
    const og = Number(ourClub.goals || 0);
    const ag = Number(oppClub.goals || 0);
    return og > ag ? t("friendly.formWin") : og < ag ? t("friendly.formLoss") : t("friendly.formDraw");
  });

  // Trend gol (ultime 12 amichevoli)
  const trendGoals = matches.slice(0, 12).reverse().map((m) => {
    const ourClub = m.data?.clubs?.[ourClubIdOf(m.data, eaClubId)];
    return Number(ourClub?.goals || 0);
  });
  const trendConceded = matches.slice(0, 12).reverse().map((m) => {
    const ourId = ourClubIdOf(m.data, eaClubId);
    const oppId = Object.keys(m.data?.clubs || {}).find((id) => id !== ourId);
    return Number((oppId ? m.data.clubs[oppId] : {})?.goals || 0);
  });

  // Aggregato per mese (chiave YYYY-MM)
  const byMonth = new Map<string, { wins: number; draws: number; losses: number; gf: number; ga: number }>();
  for (const m of matches) {
    if (!m.played_at) continue;
    const dt = new Date(m.played_at);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    const ourId = ourClubIdOf(m.data, eaClubId);
    const ourClub = m.data?.clubs?.[ourId];
    const oppId = Object.keys(m.data?.clubs || {}).find((id) => id !== ourId);
    const oppClub = oppId ? m.data.clubs[oppId] : null;
    if (!ourClub || !oppClub) continue;
    const og = Number(ourClub.goals || 0);
    const ag = Number(oppClub.goals || 0);
    const cur = byMonth.get(key) || { wins: 0, draws: 0, losses: 0, gf: 0, ga: 0 };
    cur.gf += og;
    cur.ga += ag;
    if (og > ag) cur.wins++;
    else if (og < ag) cur.losses++;
    else cur.draws++;
    byMonth.set(key, cur);
  }
  const monthRows = [...byMonth.entries()].sort((a, b) => b[0].localeCompare(a[0])).slice(0, 6);

  // Top portieri (ordinati per saves)
  const goalkeepers = stats.players
    .filter((p) => p.pos === "goalkeeper" || p.saves > 0)
    .sort((a, b) => b.saves - a.saves)
    .slice(0, 5);

  return (
    <div className="space-y-4 min-w-0">
      <Card>
        <CardContent className="p-3 sm:p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          <MatchKpi label={t("friendly.kpiTotal")} value={stats.total} />
          <MatchKpi label={t("friendly.kpiRecord")} value={t("friendly.kpiRecordValue", { w: stats.wins, d: stats.draws, l: stats.losses })} hint={t("friendly.kpiRecordHint", { pct: stats.winPct })} accent="is-accent" />
          <MatchKpi label={t("friendly.kpiGoalDiff")} value={(goalDiff >= 0 ? "+" : "") + goalDiff} hint={`${stats.goalsFor}/${stats.goalsAgainst}`} accent={goalDiff >= 0 ? "text-emerald-600" : "text-rose-600"} />
          <MatchKpi label={t("friendly.kpiGoalsPerMatch")} value={stats.total > 0 ? (stats.goalsFor / stats.total).toFixed(2) : "0"} hint={t("friendly.kpiConcededHint", { n: stats.total > 0 ? (stats.goalsAgainst / stats.total).toFixed(2) : "0" })} />
          <MatchKpi label={t("friendly.kpiCleanSheet")} value={stats.cleanSheets} hint={stats.total > 0 ? t("friendly.kpiCleanSheetHint", { pct: Math.round((stats.cleanSheets / stats.total) * 100) }) : ""} />
          <MatchKpi label={t("friendly.kpiAvgRating")} value={stats.avgRating > 0 ? stats.avgRating.toFixed(2) : "-"} hint={t("friendly.kpiAvgRatingHint")} />
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-base">{t("friendly.formTitle")}</CardTitle>
            <CardDescription>{t("friendly.formDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <div className="flex gap-1.5">
              {formData.map((label, idx) => {
                const cls =
                  label === t("friendly.formWin")
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : label === t("friendly.formDraw")
                      ? "bg-amber-100 text-amber-700 border-amber-200"
                      : label === t("friendly.formLoss")
                        ? "bg-rose-100 text-rose-700 border-rose-200"
                        : "bg-muted/50 text-muted-foreground";
                return (
                  <span
                    key={idx}
                    className={cn("h-9 w-9 grid place-items-center rounded-md border font-mono text-sm font-bold", cls)}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {trendGoals.length >= 2 && (
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3 px-4 sm:px-6">
              <CardTitle className="text-base">{t("friendly.trendTitle", { count: trendGoals.length })}</CardTitle>
              <CardDescription>
                <span className="inline-flex items-center gap-1.5 mr-3">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "hsl(var(--ok))" }} />
                  {t("friendly.trendScored")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "hsl(var(--danger))" }} />
                  {t("friendly.trendConceded")}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 sm:px-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t("friendly.trendScored")}</p>
                <AreaChart
                  values={trendGoals}
                  color="hsl(var(--ok))"
                  height={140}
                  labels={trendGoals.map((_, idx) => String(idx + 1))}
                  formatY={(v) => Math.round(v).toString()}
                />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t("friendly.trendConceded")}</p>
                <AreaChart
                  values={trendConceded}
                  color="hsl(var(--danger))"
                  height={140}
                  labels={trendConceded.map((_, idx) => String(idx + 1))}
                  formatY={(v) => Math.round(v).toString()}
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {monthRows.length > 0 && (
        <Card>
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-base">{t("friendly.monthTitle")}</CardTitle>
            <CardDescription>{t("friendly.monthDesc", { count: monthRows.length })}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 px-4 sm:px-6">
            {monthRows.map(([key, v]) => {
              const total = v.wins + v.draws + v.losses;
              const winPct = total > 0 ? Math.round((v.wins / total) * 100) : 0;
              const date = new Date(`${key}-01T00:00:00`);
              return (
                <div key={key} className="flex items-center gap-2 text-sm py-1">
                  <span className="font-mono text-xs text-muted-foreground w-24 capitalize">
                    {date.toLocaleDateString(currentIntlLocale(), { month: "long", year: "numeric" })}
                  </span>
                  <div className="flex-1 bar-stack">
                    <div style={{ width: `${total > 0 ? (v.wins / total) * 100 : 0}%`, background: "hsl(var(--ok))" }} />
                    <div style={{ width: `${total > 0 ? (v.draws / total) * 100 : 0}%`, background: "hsl(var(--warn))" }} />
                    <div style={{ width: `${total > 0 ? (v.losses / total) * 100 : 0}%`, background: "hsl(var(--danger))" }} />
                  </div>
                  <span className="font-mono text-xs tabular-nums w-32 text-right text-muted-foreground">
                    {t("friendly.monthRecord", { w: v.wins, d: v.draws, l: v.losses, pct: winPct })}
                  </span>
                  <span className="font-mono text-xs tabular-nums w-20 text-right">
                    {v.gf}-{v.ga}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3 px-4 sm:px-6">
          <CardTitle className="text-base">{t("friendly.listTitle")}</CardTitle>
          <CardDescription>{t("friendly.listDesc", { count: matches.length })}</CardDescription>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <MatchListTable teamId={teamId} matches={matches} eaClubId={eaClubId} emptyText={t("friendly.listEmpty")} />
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 min-w-0">
          <CardHeader className="pb-3 px-3 sm:px-6 flex flex-row items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base">{t("friendly.perfTitle")}</CardTitle>
              <CardDescription>{t("friendly.perfDesc", { count: stats.total })}</CardDescription>
            </div>
            <div className="inline-flex max-w-full overflow-x-auto rounded-full bg-raised p-1 text-sm">
              {(["goals", "assists", "rating", "mom", "saves"] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setSortBy(k)}
                  className={cn("px-2 py-1 rounded font-medium", sortBy === k ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground")}
                >
                  {k === "goals" ? t("friendly.sortGoals") : k === "assists" ? t("friendly.sortAssists") : k === "rating" ? t("friendly.sortRating") : k === "mom" ? t("friendly.sortMom") : t("friendly.sortSaves")}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <PlayerAggTable players={stats.players} sortBy={sortBy} rosterLookup={rosterLookup} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-base">{t("friendly.gkTitle")}</CardTitle>
            <CardDescription>{t("friendly.gkDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5 px-4 sm:px-6">
            {goalkeepers.length === 0 && <EmptyState text={t("friendly.gkEmpty")} />}
            {goalkeepers.map((g, idx) => {
              const avgR = g.ratingCount > 0 ? g.ratingSum / g.ratingCount : 0;
              return (
                <div key={g.name} className="flex items-center gap-2 text-sm">
                  <RankMedal rank={idx + 1} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{g.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {t("friendly.gkLine", { matches: g.matches, cs: g.cleanSheets, rating: avgR > 0 ? avgR.toFixed(2) : "-" })}
                    </p>
                  </div>
                  <span className="font-mono tabular-nums font-bold">{g.saves}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
